<style lang="scss" scoped>
.form-label {
    @apply text-dark font-mon text-tiny font-bold mb-16 uppercase;
    letter-spacing: 0;

    &.required {
        &:after {
            content: " *";
        }
    }
}

.default-input {
    line-height: 1.1875rem;
    padding: 19px;
    @apply border-gray rounded-sm border;
}

.error {
    @apply text-tiny;
    color: #ff0000;
    font-weight: 500;
    margin-top: 8px;
}

textarea {
    @apply border-gray p-20 rounded-sm border;
    min-height: 196px;
    resize: none;
}

small {
    color: #487a9b;
    margin-top: -6px;
    margin-bottom: 4px;
}

.fileupload {
    display: flex;
    flex-direction: column;

    input[type="file"] {
        display: none;
    }

    label.file-input {
        @apply border-gray rounded-sm border;
        display: block;
        text-align: left;
        width: 100%;
        font-weight: 400;
        line-height: 22px;
        color: #949494;
        transition: border-color 0.2s;
        padding: 22px;

        ::v-deep .link {
            color: #f5972a;
            text-decoration: underline;

            &:hover {
                cursor: pointer;
                text-decoration: none;
            }
        }

        &.highlight {
            border-color: #f5972a;
        }
    }

    .filename {
        &:after {
            content: "x";
            margin-left: 8px;
            font-weight: 700;
            opacity: 0;
            transition: opacity 0.2s;
        }

        &:hover {
            cursor: pointer;
            &:after {
                opacity: 1;
            }
        }
    }
}

.toggle {
    display: flex;
    align-items: flex-end;

    .label {
        margin-right: 16px;
    }

    input[type="checkbox"] {
        height: 0;
        width: 0;
        visibility: hidden;
    }

    label {
        cursor: pointer;
        text-indent: -9999px;
        width: 48px;
        height: 16px;
        background: #e9ebf2;
        display: block;
        border-radius: 100px;
        position: relative;
        transition: background 0.3s;
        margin-bottom: 2px;
    }

    label:after {
        content: "";
        position: absolute;
        top: -4px;
        left: 0;
        width: 24px;
        height: 24px;
        background: #fff;
        border-radius: 50%;
        transition: 0.3s;
    }

    input:checked + label {
        background: #23b32f;
    }

    input:checked + label:after {
        left: 100%;
        transform: translateX(-100%);
    }
}

.radio {
    label {
        font-size: 1rem;
        margin-right: 32px;
        margin-left: 4px;
        margin-bottom: 0;
    }

    input {
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome, Edge, Opera and Firefox */
    }

    .options {
        display: flex;

        .option {
            display: flex;
            align-items: center;
        }
    }
}

.checkbox {
    display: flex;
    align-items: center;

    input {
        transform: translateY(-1px);
        margin-right: 8px;
    }

    label {
        color: #1e1e1e;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 16px;
        margin-bottom: 0;
        text-transform: unset;
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome, Edge, Opera and Firefox */

    }
}
</style>

<template>
    <ValidationProvider ref="provider" :vid="name || label.toLowerCase()" :name="label" :rules="rules" :bails="false" v-slot="{ classes, errors }" tag="div" class="form-group flex flex-col">
        <label v-if="label.length > 0 && type !== 'fileupload' && type !== 'toggle' && type !== 'checkbox' && type !== 'radio'" :for="`id_${name || label.toLowerCase()}`" :class="[classes, 'form-label']">{{ label }}</label>
        <small v-if="description.length > 0 && type !== 'fileupload'" :id="`description_${name || label.toLowerCase()}`" class="font-light">{{ description }}</small>
        <textarea
            v-if="type === 'textarea'"
            v-model="localValue"
            v-on="{ ...$listeners, input: update }"
            class="default-input"
            :class="classes"
            :id="`id_${name || label.toLowerCase()}`"
            :aria-describedby="`description_${name || label.toLowerCase()}`"
            :placeholder="placeholder || `${label}`"
            cols="3"
        ></textarea>
        <div v-else-if="type === 'fileupload'" class="fileupload">
            <span class="label form-label" :class="classes">{{ label }}</span>
            <small v-if="description.length > 0" :id="`description_${name || label.toLowerCase()}`" class="font-light">{{ description }}</small>
            <label id="drop-area" class="d-block file-input" for="fileinput" v-html="placeholder"></label>
            <input type="file" id="fileinput" :multiple="multiple" ref="fileinput" @change="handleFileUpload()" :accept="inputAccept" />
            <span v-for="(file, index) in Array.from(localValue)" :key="index" class="filename" @click="clearUpload(file.name)">{{ file.name }}</span>
        </div>
        <div v-else-if="type === 'toggle'" class="toggle">
            <span class="label form-label" :class="labelClasses">{{ label }}</span>
            <input type="checkbox" v-model="localValue" :id="`id_${name || label.toLowerCase()}`" @change="update" />
            <label for="toggle" @click="toggle">Toggle</label>
        </div>
        <div v-else-if="type === 'checkbox'" class="checkbox">
            <input type="checkbox" v-model="localValue" :id="`id_${name || label.toLowerCase()}`" v-on="{ ...$listeners, input: updateInverted }" />
            <label :for="`id_${name || label.toLowerCase()}`" v-html="label"></label>
        </div>
        <div v-else-if="type === 'radio'" class="radio">
            <span v-if="label" class="label" :class="labelClasses">{{ label }}</span>
            <div class="options">
                <div class="option" v-for="option in options" :key="option">
                    <input type="radio" :id="option" :name="name" v-model="localValue" v-on="{ ...$listeners, input: update }" />
                    <label :for="option">{{ option }}</label>
                </div>
            </div>
        </div>
        <input
            v-else
            :type="type"
            v-model="localValue"
            v-on="{ ...$listeners, input: update }"
            class="default-input"
            :id="`id_${name || label.toLowerCase()}`"
            :aria-describedby="`description_${name || label.toLowerCase()}`"
            :placeholder="placeholder || `${label}`"
        />
        <label v-if="errors[0]" class="error">{{ errors[0] }}</label>
    </ValidationProvider>
</template>

<script>
import HasInput from "@/mixins/components/has-input";
import { ValidationProvider, validate } from "vee-validate";

export default {
    name: "Forms.Elements.ElInput",

    mixins: [HasInput],

    components: { ValidationProvider },

    props: {
        type: {
            type: String,
            default: "text",
            validator: val => {
                return !!["text", "email", "date", "password", "textarea", "number", "radio", "fileupload", "checkbox", "toggle"].includes(val);
            }
        }
    },

    mounted() {
        const self = this;

        if (this.type === "textarea") {
            // auto resize textarea
            var tx = document.getElementsByTagName("textarea");
            for (var i = 0; i < tx.length; i++) {
                tx[i].setAttribute("style", "height:" + tx[i].scrollHeight + "px;overflow-y:hidden;");
                tx[i].addEventListener("input", OnInput, false);
            }

            function OnInput() {
                this.style.height = "auto";
                this.style.height = this.scrollHeight + "px";
            }
        }

        if (this.type === "fileupload") {
            // drop behaviour
            let dropArea = document.getElementById("drop-area");

            ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
                dropArea.addEventListener(eventName, preventDefaults, false);
            });

            function preventDefaults(e) {
                e.preventDefault();
                e.stopPropagation();
            }

            ["dragenter", "dragover"].forEach(eventName => {
                dropArea.addEventListener(eventName, highlight, false);
            });

            ["dragleave", "drop"].forEach(eventName => {
                dropArea.addEventListener(eventName, unhighlight, false);
            });

            function highlight(e) {
                dropArea.classList.add("highlight");
            }

            function unhighlight(e) {
                dropArea.classList.remove("highlight");
            }

            dropArea.addEventListener("drop", handleDrop, false);

            function handleDrop(e) {
                let dt = e.dataTransfer;
                let files = dt.files;

                self.$refs.fileinput.files = files;

                const uploadedFiles = Array.from(files);

                if (self.localValue.length > 0) {
                    uploadedFiles.forEach(f => {
                        var matches = self.localValue.filter(v => {
                            return v.name === f.name;
                        });
                        if (matches.length === 0) {
                            self.localValue.push(f);
                        }
                    });
                } else {
                    self.localValue = uploadedFiles;
                }

                self.update();
            }
        }
    },

    methods: {
        handleFileUpload() {
            const self = this;
            const uploadedFiles = Array.from(self.$refs.fileinput.files);

            if (self.localValue.length > 0) {
                if (this.localValue.length + uploadedFiles.length > self.maxFiles) {
                    alert(`Je kan nog maximaal ${self.maxFiles - this.localValue.length} bestanden uploaden. Verwijder één of meerdere bestanden en probeer opnieuw.`);
                } else {
                    uploadedFiles.forEach(f => {
                        var matches = self.localValue.filter(v => {
                            return v.name === f.name;
                        });
                        if (matches.length === 0) {
                            // only upload when file with same name is not present
                            this.tryAddFile(f);
                        }
                    });
                }
            } else {
                self.localValue = [];
                uploadedFiles.forEach(f => {
                    this.tryAddFile(f);
                });
            }

            self.update();
        },

        tryAddFile(file) {
            if (file.size > 2097152) {
                alert(`Bestand ${file.name} is groter dan de maximumgrootte (2MB)`);
            } else {
                if (this.multiple) {
                    this.localValue.push(file);
                } else {
                    this.localValue = [file];
                }
            }
        },

        clearUpload(name) {
            this.localValue = this.localValue.filter(v => {
                return v.name != name;
            });
            this.update();
        }
    }
};
</script>
